'use client';

import { useState } from 'react';
import { createSPAClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { CheckCircle, Mail } from 'lucide-react';
import { captureClientError } from '@/lib/error-reporting/client';

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const supabase = createSPAClient();
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });

      if (error) throw error;

      setSuccess(true);
    } catch (err) {
      captureClientError(err, 'client_runtime', {
        component: 'ForgotPasswordPage',
        action: 'resetPasswordForEmail',
      }).catch(() => {});
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError('An unknown error occurred');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="space-y-6">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600 opacity-20 blur-xl animate-pulse" />
              <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400/20 to-amber-600/20 border border-yellow-500/30">
                <CheckCircle className="w-10 h-10 text-yellow-400" />
              </div>
            </div>
          </div>

          <h3 className="text-2xl font-bold text-white mb-3">Check your email</h3>
          <p className="text-gray-400 text-sm max-w-sm mx-auto">
            We&apos;ve sent a password reset link to{' '}
            <span className="text-yellow-400 font-medium break-all">{email}</span>. Follow the
            instructions to reset your password.
          </p>
        </div>

        <div className="text-center text-sm border-t border-yellow-500/20 pt-6">
          <Link
            href="/auth/login"
            className="font-medium text-yellow-400 hover:text-yellow-300 transition-colors"
          >
            Return to login
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="text-center">
        <div className="flex justify-center mb-6">
          <div className="relative">
            <div className="absolute inset-0 rounded-full bg-gradient-to-r from-yellow-400 to-amber-600 opacity-20 blur-xl animate-pulse" />
            <div className="relative flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-yellow-400/20 to-amber-600/20 border border-yellow-500/30">
              <Mail className="w-10 h-10 text-yellow-400" />
            </div>
          </div>
        </div>
        <h3 className="text-2xl font-bold text-white mb-2">Reset your password</h3>
        <p className="text-gray-400 text-sm">
          Enter your email and we&apos;ll send you a reset link
        </p>
      </div>

      {error && (
        <div className="p-4 text-sm text-red-300 bg-red-900/20 border border-red-500/30 rounded-lg">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-5">
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-white mb-2">
            Email address
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com"
            className="block w-full appearance-none rounded-lg border border-yellow-500/20 bg-black/50 px-4 py-3 text-white placeholder:text-gray-500 shadow-sm focus:border-yellow-500/50 focus:outline-none focus:ring-2 focus:ring-yellow-500/30 transition-all"
          />
        </div>

        <div>
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center rounded-lg border border-transparent bg-gradient-to-r from-yellow-400 to-amber-600 py-3 px-4 text-sm font-bold text-black shadow-lg hover:from-yellow-500 hover:to-amber-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-offset-2 focus:ring-offset-black disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            {loading ? 'Sending reset link...' : 'Send reset link'}
          </button>
        </div>
      </form>

      <div className="text-center text-sm border-t border-yellow-500/20 pt-6">
        <span className="text-gray-400">Remember your password?</span>{' '}
        <Link
          href="/auth/login"
          className="font-medium text-yellow-400 hover:text-yellow-300 transition-colors"
        >
          Sign in
        </Link>
      </div>
    </div>
  );
}
